import React, { useState, useEffect } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Card,
  CardContent,
  Snackbar,
} from '@mui/material';
import { api } from '../../services/api.js';
import { mapServerResearchToLocal, pollResearchStatus, isPending } from '../../services/apiUtils.js';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../../styles/Theme.js';
import { useAuth } from '../../authentication/AuthContext.js';
import { useNavigate } from 'react-router-dom';
import { surveyLimitService } from '../../services/surveyLimitService.js';
import { event } from '../../services/analyticsService.js';
import SurveyEditor from './SurveyEditor.js';
import SurveyPromptEditor from './SurveyPromptEditor.js';
import { enoughCredits } from './SurveyUtils.js';
import ResearchTypeSelector from '../../components/ResearchTypeSelector.js';


// Research types definition
const researchTypes = [
  {
    id: 'general',
    name: 'General Survey',
    description: 'Conduct a general survey study tailored to your specific needs. This option is flexible and can be customized for any survey objective.',
    example: 'Example: "I want to understand consumer preferences for online learning platforms, including preferred features, pricing models, and content delivery methods."',
    icon: 'assessment'
  },
  {
    id: 'product-validation',
    name: 'Product Validation',
    description: 'Quickly assess the viability and appeal of your startup concept, new product, or identified market problem. This ensures you focus on solutions that genuinely meet customer needs, minimizing risk and investment uncertainty.',
    example: 'Example: "I want to validate my idea for a meal planning app that automatically generates grocery lists based on dietary preferences and budget constraints. I want to understand whether users find this concept appealing and what features they would prioritize. I also want questions on what type of customers this appeals to most"',
    icon: 'check_circle'
  },
  {
    id: 'branding-feedback',
    name: 'Branding Feedback',
    description: 'Understand how your brand identity is perceived by your intended audience. You want to find out how to optimize brand perception, build stronger emotional connections, and enhance overall brand loyalty.',
    example: 'Example: "I want feedback on my eco-friendly clothing brand\'s new slogan and the messaging we have around sustainability, to see how it resonates with environmentally conscious consumers. We are targeting a Gen Z audience, so give me questions that will help me understand how to optimize our brand\'s perception with them."',
    icon: 'feedback'
  },
  {
    id: 'campaign-validation',
    name: 'Campaign Idea Validation',
    description: 'Test campaign ideas, messages, or concepts before launching them widely. Your goal is to ensure your marketing efforts resonate effectively with your audience, maximizing campaign impact and ROI.',
    example: 'Example: "I want to test my social media campaign concept for promoting our new fitness tracker, focusing on the \'30-day transformation challenge\' messaging. We want ideas on where to focus our marketing efforts, given our target audience of health-conscious elders."',
    icon: 'campaign' 
  }
];

function Step1Survey({ userData, setUserData, onTopicUpdate, setTabIndex, progress, setProgress, onDuplicateResearch, unsavedSurveyChanges,setUnsavedSurveyChanges, resetLoading, readOnly }) {
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);
  const [openWarningSnackbar, setOpenWarningSnackbar] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [researchType, setResearchType] = useState('general'); // Default to general survey
  
  const { walletBalance, isAdmin,hasActiveSubscription, credits, updateUserAttributes } = useAuth();
  const navigate = useNavigate();

// Effect to poll for survey status
useEffect(() => {
  console.log("Inside useeffect for polling, loading is", loading, "polling is ", polling);
  if (userData.surveyStatus === "PENDING" && userData.researchID) {
    console.log("🔥 POLLING useEffect triggered - Survey Status:", userData.surveyStatus);
    setLoading(true);
    setPolling(true);
    const pollForSurvey = async () => {
      try {
        const research = await pollResearchStatus(userData.userID, userData.researchID, 'surveyStatus');
        const localResearch = mapServerResearchToLocal(research);
        const newUserData = { ...userData, ...localResearch };
        setUserData(newUserData);
        onTopicUpdate(newUserData);

      } catch (err) {
        console.log("Error when waiting for survey generation");
        setError(`Error polling for survey: ${err.message}`);
      } finally {
        console.log("finally block triggered");
        setLoading(false);
        setPolling(false);
      }
    };
    pollForSurvey();
  } 
}, [userData.surveyStatus, userData.userID, userData.researchID, userData, loading, onTopicUpdate, setUserData]);


  useEffect(() => {
    setLoading(false);
    setPolling(false);
    
  }, [resetLoading, userData.researchID]);


const hitSurveyLimit = async () => {
  if (readOnly) return true;
  setError(null);
  // Track survey submission
  event({
    action: 'research_step_started',
    category: 'tool_usage', 
    label: 'step_1_survey_creation',
  });
  // Check survey limits
  // In Step1Survey.js handleSubmit function
  const eligibility = await surveyLimitService.canCreateSurvey(userData, { isAdmin, credits }); 
  console.log("eligibility is", eligibility);
  if (!eligibility.canCreate) {

      // Show enhanced error message with subscription link
      setError(eligibility.message);
      // Show warning snackbar with action button
      setWarningMessage("We are currently welcoming beta testers and giving them unrestricted access to the platform. <a href='/become-beta'>Request access</a>.");
      setOpenWarningSnackbar(true);
      setLoading(false);
      return true;
    }
    return false;
 }

const handleSubmit = async (e) => {
  e.preventDefault();
  if (readOnly) return;
  setLoading(true);


  if (await hitSurveyLimit()) {
    return;
  }

  let result;
  try {
    // If we reach here, user is eligible (either free, paid, or admin)
    result = await api.createSurvey(userData.userID, userData.description, researchType);
  } catch (err) {
    // Track error
    event({
      action: 'research_step_error',
      category: 'error',
      label: `survey_creation_error: ${err.message}`,
    });
    setError("Error on the server, please try again later or contact us if this issue keeps reoccurring");
    return;
  }
  try {
    console.log("INSIDE HANDLE SUBBMIT, result is", result);
    const researchID = result.researchID; 
    const title = result.title;

    // Update last used date
    await surveyLimitService.updateLastUsedDate();
    
    const newTopic = {...userData, 
      researchID: researchID,
      title: title,
      description: userData.description,
      researchType: researchType,
      surveyStatus: 'PENDING',
      lastModified: Date.now() / 1000,
      responsesStatus: 'NOT_STARTED',
      personasStatus: 'NOT_STARTED'
    }

    
    setUserData(newTopic);


    // Immediately update the topics list with the new topic
    // We'll use onTopicUpdate to update the parent component's state
    if (onTopicUpdate) {
      onTopicUpdate(newTopic);
    }
  } catch (err) {
    setError("An error occurred after creating your survey, please try again or contact us if this issue keeps reoccurring");
    return;
  }

};

  const emotionCache = createCache({
    key: 'mui-style',
    prepend: true,
  });

  const handleRegenerateSurvey = async () => {
  if (readOnly) return;
  let result;
  try {
    // If we reach here, user is eligible (either free, paid, or admin)
    result = await api.regenerateSurvey(userData.userID, userData.researchID, userData.description, userData.researchType);
  } catch (err) {
    // Track error
    event({
      action: 'research_step_error',
      category: 'error',
      label: `survey_creation_error: ${err.message}`,
    });
    setError("Error on the server, please try again later or contact us if this issue keeps reoccurring");
    return;
  }
  try {
    console.log("INSIDE HANDLE REGENERATE, result is", result);

    // Update last used date
    await surveyLimitService.updateLastUsedDate();
    
    const newTopic = {...userData, 
      surveyStatus: 'PENDING',
      responsesStatus: result.responsesStatus,
      lastModified: Date.now() / 1000,
      surveyQuestions: []
    }

    
    setUserData(newTopic);


    // Immediately update the topics list with the new topic
    // We'll use onTopicUpdate to update the parent component's state
    if (onTopicUpdate) {
      onTopicUpdate(newTopic);
    }
  } catch (err) {
    setError("An error occurred after regenerating your survey, please try again or contact us if this issue keeps reoccurring");
    return;
  }
  }

  // ---- prompt regeneration ----
  const beforeRegenerateSurvey = async () => {
    if (readOnly) return;
    if (isPending(userData, setWarningMessage, setOpenWarningSnackbar)) {
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log('Regenerate survey with prompt:', userData.description);

    if (await hitSurveyLimit()) {
      setLoading(false);
      return;
    }
    try {
      const hasCredits = await enoughCredits(progress, isAdmin, hasActiveSubscription, walletBalance);
      // If no credits and no subscription, show error
      if (!hasCredits) {
        setWarningMessage(
          "Please sign up to our beta to get unrestricted access to our platform"
        );
        setOpenWarningSnackbar(true);
        setLoading(false);
        return;

      }
    } catch (error) {
      console.error("Error checking credits:", error);
    }


    const hasResponses = userData.surveyResponses && userData.surveyResponses.length > 0;
    if (hasResponses) {
      // If there are responses, warn about credit cost
      const proceed = window.confirm(
        'You already have responses, you will have to re-generate those after a new survey is created. Do you want to proceed?'
      );
      if (proceed) {
        handleRegenerateSurvey();
      } else {
        setLoading(false);
        return;
      }
    } else {
      // If no responses, just save without warning
      handleRegenerateSurvey();
    }
  };

  /* --------------- State helpers ------------------------------------- */
  const hasResearchID       = Boolean(userData.researchID);
  const hasSurveyQuestions  = Boolean(userData.surveyQuestions?.length);

  /*
   * The page can only be in one of three states:
   * 1.  INITIAL      – no researchID, no questions
   * 2.  GENERATING   – researchID,   no questions
   * 3.  READY        – researchID,   questions
   */
  const pageState = !hasResearchID && !hasSurveyQuestions
    ? 'INITIAL'
    : hasResearchID && (!hasSurveyQuestions || loading)
      ? 'GENERATING'
      : 'READY';

  /* ---------------- Render ------------------------------------------- */
  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={ResearchTheme}>
        <Box data-tabpanel="survey">
          <Typography variant="h5" gutterBottom>
            Create Survey
          </Typography>

          {/* --------- STATE 1 : no id, no questions -------------------- */}
          {pageState === 'INITIAL' && (
            readOnly ? (
               <Paper sx={{ p: 3, mt: 2 }}>
                 <Typography align="center" color="text.secondary">
                   No survey data available.
                 </Typography>
               </Paper>
            ) : (
            <>
              {/* ---- Research-type cards ---- */}

                <ResearchTypeSelector
                  types={researchTypes}
                  value={researchType}
                  onChange={setResearchType}
                />

              {/* ---- Description form ---- */}
              <Paper sx={{ p: 3, mt: 2 }}>
                <Box component="form" onSubmit={handleSubmit}>

                  <TextField
                    fullWidth
                    label="I want a survey about …"
                    multiline
                    rows={4}
                    value={userData.description}
                    onChange={e =>
                      setUserData(prev => ({ ...prev, description: e.target.value }))
                    }
                    margin="normal"
                    required
                  />

                  {error && (
                    <Typography color="error" sx={{ mt: 2 }}>
                      Error: {error}
                    </Typography>
                  )}

                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Generate Survey'}
                  </Button>
                </Box>
              </Paper>
            </>
            )
          )}

          {/* --------- STATE 2 : id, no questions ----------------------- */}
          {pageState === 'GENERATING' && (
            <> 
              <SurveyPromptEditor
                userData={userData}
                setUserData={setUserData}
                onRegenerate={beforeRegenerateSurvey}
                setWarningMessage={setWarningMessage}
                setOpenWarningSnackbar={setOpenWarningSnackbar}
                onTopicUpdate={onTopicUpdate}
                researchTypes={researchTypes}
                loading={loading}
                polling={true}
                readOnly={readOnly}
              />
            
            <Paper sx={{ p: 3, mt: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <CircularProgress size={40} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Generating your survey…
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This may take a minute or two. Please wait.
                </Typography>
              </Box>
            </Paper>


            </>

          )}

          {/* --------- STATE 3 : id & questions ------------------------- */}
          {pageState === 'READY' && (
            <>
              <SurveyPromptEditor
                userData={userData}
                setUserData={setUserData}
                onRegenerate={beforeRegenerateSurvey}
                setWarningMessage={setWarningMessage}
                setOpenWarningSnackbar={setOpenWarningSnackbar}
                onTopicUpdate={onTopicUpdate}
                researchTypes={researchTypes}
                loading={loading}
                polling={polling}
                readOnly={readOnly}
              />

              <SurveyEditor
                userData={userData}
                setUserData={setUserData}
                onTopicUpdate={onTopicUpdate}
                setTabIndex={setTabIndex}
                progress={progress}
                setProgress={setProgress}
                onDuplicateResearch={onDuplicateResearch}
                isAdmin={isAdmin}
                hasActiveSubscription={hasActiveSubscription}
                walletBalance={walletBalance}
                credits={credits}
                updateUserAttributes={updateUserAttributes}
                unsavedSurveyChanges={unsavedSurveyChanges}
                setUnsavedSurveyChanges={setUnsavedSurveyChanges}
                readOnly={readOnly}
              />
            </>
          )}

          {/* ---------- Snackbar (visible in every state) --------------- */}
          <Snackbar
            open={openWarningSnackbar}
            autoHideDuration={6000}
            onClose={() => setOpenWarningSnackbar(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={() => setOpenWarningSnackbar(false)}
              severity="warning"
              action={
                !readOnly && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate('/account', { state: { subscribe: true } })}
                >
                  Go to Account
                </Button>
                )
              }
            >
              {warningMessage}
            </Alert>
          </Snackbar>
        </Box>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default Step1Survey;
